/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useState } from 'react'
import Tips from '../common/Tips'
import Loading from '../common/Loading'
import { sortBy } from 'lodash'
import { secToHour, secToHourMinute } from '../../../utils/date'
import { setPath, setUrlId } from '../../../utils/url'
import { Link, useParams } from 'react-router-dom'
import { LangValue } from '../../../states/types/common'
import { URL } from '../../constants/url'
import { useTranslation } from 'react-i18next'
import { TeamReportWorkDetail } from '../../../states/types/team'
import InfoTips from '../common/InfoTips'

interface Props {
  isWorkDetailLoading: boolean | undefined
  workDetail: TeamReportWorkDetail | undefined
}

interface MemberDetail {
  id: number
  name: string
  profile_image_url?: string | null
  categories: ListDetail[]
  applications: ListDetail[]
}

interface ListDetail {
  id: number
  name: string
  code: string | null
  color: string
  time: number
}

const WorkingDetail: FC<Props> = (props) => {
  const {
    isWorkDetailLoading,
    workDetail,
  } = props

  const { language } = useParams<{ language: LangValue }>()
  const { t } = useTranslation()

  const DISPLAY_LIMIT_NUM = 5

  const [isSelectCategory, setIsSelectCategory] = useState<boolean>(true)
  const [hoverName, setHoverName] = useState<string>("")
  const [hoverMemberId, setHoverMemberId] = useState<number>(0)

  const [data, setData] = useState<MemberDetail[]>([])

  const calcList = (list: ListDetail[]) => {
    let tmpList: { id: number; name: string; color: string; time: number; cost: number }[] = []
    list.forEach((v) => {
      if (!tmpList.map((w) => w.name).includes(v.name)) {
        tmpList.push({
          id: v.id,
          name: v.name,
          color: v.color,
          time: v.time,
          cost: 0,
        })
      } else {
        tmpList = tmpList.map((w) => {
          if (w.name === v.name) return { ...w, time: w.time + v.time }
          return w
        })
      }
    })
    return tmpList
  }

  const categoryList = useMemo(() => {
    if (!data) return []
    return workDetail?.category_chart.labels.map((label, i) => (
      {
        name: t(`parts.category_name.${label}`),
        color: workDetail?.category_chart.colors[i],
      }
    ))
  }, [data])

  const applicationList = useMemo(() => {
    if (!data) return []
    return calcList(data.map((v) => v.applications).flat())
  }, [data])

  const legendList = useMemo(() => {
    if (!categoryList || !applicationList) return []
    if (isSelectCategory) {
      return categoryList || []
    }
    let tmpArray = sortBy(applicationList.filter((v) => v.name !== "操作なし"), "time").reverse().filter((v) => v.time > 0)
    const noOperation = applicationList.find((v) => v.name === "操作なし")
    if (noOperation) {
      tmpArray = [...tmpArray, noOperation]
    }
    return tmpArray.map((v) => ({ name: v.name, color: v.color }))
  }, [categoryList, applicationList, isSelectCategory])

  const maxTime = useMemo(() => {
    if (!data) return 0
    const tmpMax = Math.max(...data.map((v) => {
      if (isSelectCategory) return v.categories.reduce((s, w) => s + w.time, 0)
      return v.applications.reduce((s, w) => s + w.time, 0)
    }))
    return (secToHour(tmpMax) + 1) * 3600
  }, [data, isSelectCategory])

  const buildDisplayGraphOrder = (list: ListDetail[]) => (
    legendList.map((v) => {
      const tmpDetails = list.filter((w) => w.name === v.name)
      if (tmpDetails.length) {
        return {
          ...tmpDetails[0],
          time: tmpDetails.reduce((s, w) => s + w.time, 0),
        }
      }
      return undefined
    }).filter((v):v is NonNullable<typeof v> => !!v)
  )

  const buildDisplayDetail = (memberDetail:MemberDetail) => (
    isSelectCategory ? memberDetail.categories : memberDetail.applications
  )

  const onMouseOverGraph = (id: number, name: string) => {
    setHoverName(name)
    setHoverMemberId(id)
  }

  const onMouseLeaveGraph = () => {
    setHoverName("")
    setHoverMemberId(0)
  }

  const buildMemberReportUrl = (id: number) => setPath(setUrlId(URL.RISK_MEMBER_DETAIL.path, 'id', id), language)

  useEffect(() => {
    if (!workDetail) return
    const tmp = workDetail.member_ids.map((memberId, i) => (
      {
        id: memberId,
        name: workDetail.bar_categories[i],
        profile_image_url: null,
        categories: workDetail.category_chart.data.map((v, j) => (
          {
            id: j,
            name: t(`parts.category_name.${v.name}`),
            code: null,
            color: workDetail.category_chart.colors[j],
            time: v.data[i],
          }
        )),
        applications: workDetail.application_chart.app.map((v, j) => (
          {
            id: j,
            name: v === "no_operation" ? "操作なし" : v,
            code: null,
            color: workDetail.application_chart.color[j],
            time: workDetail.application_chart.value[j].data[i],
          }
        )),
      }
    ))
    setData(tmp)
  }, [workDetail])

  return (
    <>
      <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
        <div className="box summary">
          <div style={{ textAlign: "center", paddingTop: "1em", paddingBottom: "1em" }}>
            <span style={{ fontWeight: "bold", fontSize: "24px", color: "#094E8D" }}>{t("parts.work_detail.title")}</span>
          </div>
          <InfoTips>
            <>
              <div>
                ユーザーの作業時間を分類別・アプリケーション別に集計・表示しています
              </div>
            </>
          </InfoTips>
          <div style={{ marginBottom: "1em" }}>
            <div className="category_title_area">
              <div className="category_title" />
              <div className="switch_area">
                <button
                  className={isSelectCategory ? "select" : ""}
                  type="button"
                  onClick={() => setIsSelectCategory(true)}
                >
                  分類別
                </button>
                <button
                  className={isSelectCategory ? "" : "select"}
                  type="button"
                  onClick={() => setIsSelectCategory(false)}
                >
                  アプリケーション別
                </button>
              </div>
            </div>
            {
              isWorkDetailLoading ? (
                <Loading />
              ) : (
                <>
                  <div className="list_area">
                    <div className="legend_area">
                      {
                        legendList.map((v) => (
                          <div
                            key={`legend_${v.name}_${v.color}_${isSelectCategory}`}
                            className="legend"
                            onMouseOver={() => setHoverName(v.name)}
                            onFocus={() => { /* */ }}
                            onMouseLeave={() => setHoverName("")}
                            role="none"
                          >
                            <div
                              className="color_box"
                              style={{
                                backgroundColor: v.color,
                              }}
                            />
                            {v.name}
                          </div>
                        ))
                      }
                    </div>
                    {
                      data?.map((v) => (
                        <>
                          <div className="list_row">
                            <div className={`name_area ${(!hoverMemberId || hoverMemberId === v.id) ? "" : "transparent"}`}>
                              {
                                v.profile_image_url ? (
                                  <img alt="" src={v.profile_image_url} className="profile_icon" />
                                ) : (
                                  <img alt="" src={`/static/images/profile/${(v.id || 0) % 12}.jpeg`} className="profile_icon" />
                                )
                              }
                              <Link to={buildMemberReportUrl(v.id)}>{v.name}</Link>
                            </div>
                            <div className="graph_area">
                              <div
                                className="graph_wrap"
                                style={{
                                  width: `${(buildDisplayDetail(v).reduce((s, val) => s + val.time, 0) / maxTime) * 100}%`,
                                }}
                              >
                                {
                                  buildDisplayGraphOrder(buildDisplayDetail(v)).map((w) => (
                                    <React.Fragment key={`${w.id}_${w.name}_${w.time}`}>
                                      <div
                                        className={`graph_detail ${(!hoverName || (!hoverMemberId && hoverName === w.name) || (hoverMemberId === v.id && hoverName === w.name)) ? "" : "transparent"}`}
                                        style={{
                                          width: `${(w.time / buildDisplayDetail(v).reduce((s, val) => s + val.time, 0)) * 100}%`,
                                          background: `linear-gradient(to right, ${w.color} 0 calc(100% - 1px), #FFFFFF calc(100% - 1px) 100%)`,
                                        }}
                                        onMouseOver={() => onMouseOverGraph(v.id, w.name)}
                                        onFocus={() => { /* */ }}
                                        onMouseLeave={() => onMouseLeaveGraph()}
                                      >
                                        <Tips
                                          description={`${w.name}%br%${secToHourMinute(w.time)} (${Math.floor((w.time / buildDisplayDetail(v).reduce((s, val) => s + val.time, 0)) * 1000) / 10}%)`}
                                          className="inner_graph"
                                          icon={false}
                                        >
                                          <div
                                            className="graph_text"
                                            style={{
                                              color: w.color,
                                            }}
                                          >
                                            {
                                              (w.time / maxTime) * 100 > DISPLAY_LIMIT_NUM && (
                                                <>
                                                  <div
                                                    style={{
                                                      fontSize: "14px",
                                                    }}
                                                  >
                                                    {secToHourMinute(w.time)}
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: "10px",
                                                    }}
                                                  >
                                                    {`(${Math.floor((w.time / buildDisplayDetail(v).reduce((s, val) => s + val.time, 0)) * 1000) / 10}%)`}
                                                  </div>
                                                </>
                                              )
                                            }
                                          </div>
                                        </Tips>
                                      </div>
                                    </React.Fragment>
                                  ))
                                }
                              </div>
                            </div>
                          </div>
                        </>
                      ))
                    }
                    <div className="ruler_area">
                      <div
                        className="scale_area"
                        style={{
                          left: 0,
                        }}
                      >
                        <div className="scale" />
                        {secToHourMinute(0)}
                      </div>
                      {
                        Array(5).fill(null).map((_v, i) => (
                          <div
                            className="scale_area"
                            style={{
                              left: `${(100 / 5) * (i + 1)}%`,
                            }}
                          >
                            <div className="scale" />
                            {secToHourMinute((maxTime / 5) * (i + 1))}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </>
              )
            }
          </div>
        </div>
      </div>
    </>
  )
}

export default WorkingDetail
